// Pure helpers for Marketing → Meta Ads → Product Sets. No React, no API
// calls: everything here is covered by metaAdsModel.test.js.

export const META_ADS_PATH = "/admin/marketing/meta-ads";
export const productSetPath = (id) => `${META_ADS_PATH}/product-sets/${id}`;

export const PICKER_PAGE_SIZE = 48;
export const NAME_MAX_LENGTH = 120;
export const DESCRIPTION_MAX_LENGTH = 500;

export const fill = (template, values = {}) =>
  Object.entries(values).reduce(
    (text, [key, value]) => text.split(`{${key}}`).join(String(value)),
    String(template || "")
  );

/** `plural(tt, "selectionCount", 1)` → "1 product colour"; picks `<key>One` / `<key>Other`. */
export const plural = (tt, key, count, values = {}) =>
  fill(tt(`${key}${Number(count) === 1 ? "One" : "Other"}`), { count, ...values });

// ── Money / time ─────────────────────────────────────────────────────────────

export const formatPrice = (value) => {
  if (value === null || value === undefined || value === "") return "";
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return "";
  return `€${Number.isInteger(parsed) ? parsed : parsed.toFixed(2)}`;
};

const RELATIVE_STEPS = [
  { unit: "year", seconds: 365 * 24 * 3600 },
  { unit: "month", seconds: 30 * 24 * 3600 },
  { unit: "week", seconds: 7 * 24 * 3600 },
  { unit: "day", seconds: 24 * 3600 },
  { unit: "hour", seconds: 3600 },
  { unit: "minute", seconds: 60 },
];

// Browsers ship little or no Albanian data for Intl.RelativeTimeFormat (it
// silently falls back to English), so Albanian past times are spelled out here.
const ALBANIAN_UNITS = {
  year: ["vit", "vjet"],
  month: ["muaj", "muaj"],
  week: ["javë", "javë"],
  day: ["ditë", "ditë"],
  hour: ["orë", "orë"],
  minute: ["minutë", "minuta"],
};

const formatAlbanianPast = (amount, unit) => {
  if (unit === "day" && amount === 1) return "dje";
  const [one, other] = ALBANIAN_UNITS[unit];
  return `${amount} ${amount === 1 ? one : other} më parë`;
};

/** "12 minutes ago" / "12 minuta më parë"; `justNow` under a minute. */
export const formatRelativeTime = (value, language = "sq", now = Date.now(), justNow = "") => {
  if (!value) return "";
  const time = new Date(value).getTime();
  if (!Number.isFinite(time)) return "";
  const diffSeconds = Math.round((time - now) / 1000);
  if (Math.abs(diffSeconds) < 60 && justNow) return justNow;

  const step = RELATIVE_STEPS.find((entry) => Math.abs(diffSeconds) >= entry.seconds) || RELATIVE_STEPS[5];
  const amount = Math.round(diffSeconds / step.seconds);
  if (language !== "en" && amount <= 0) {
    return formatAlbanianPast(Math.max(1, Math.abs(amount)), step.unit);
  }
  try {
    return new Intl.RelativeTimeFormat(language === "en" ? "en" : "sq", { numeric: "auto" }).format(
      amount,
      step.unit
    );
  } catch (_error) {
    return new Date(time).toLocaleString();
  }
};

export const formatExactTime = (value, language = "sq") => {
  if (!value) return "";
  try {
    return new Intl.DateTimeFormat(language === "en" ? "en-GB" : "sq-AL", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }).format(new Date(value));
  } catch (_error) {
    return String(value);
  }
};

// ── Selection ────────────────────────────────────────────────────────────────

/** What saving would change, comparing the saved ids with the current ones. */
export const diffSelection = (savedIds, selectedIds) => {
  const saved = savedIds instanceof Set ? savedIds : new Set(savedIds || []);
  const selected = selectedIds instanceof Set ? selectedIds : new Set(selectedIds || []);
  const added = [...selected].filter((id) => !saved.has(id));
  const removed = [...saved].filter((id) => !selected.has(id));
  return { added, removed, count: added.length + removed.length, isDirty: added.length + removed.length > 0 };
};

/**
 * Tri-state for "select all matching": every id matching the current filters
 * (across all pages, not only the rendered ones).
 */
export const getBulkSelectionState = (matchingKeys, selectedKeys) => {
  const ids = matchingKeys || [];
  if (!ids.length) return "none";
  const selectedCount = ids.reduce((count, id) => count + (selectedKeys.has(id) ? 1 : 0), 0);
  if (selectedCount === 0) return "none";
  return selectedCount === ids.length ? "all" : "some";
};

/**
 * Order for the "Selected" view: products added in this session first (most
 * recent on top), then the saved ones in the server's order (newest first).
 */
export const orderSelectedForView = (selectedIds, savedIds) => {
  const saved = savedIds instanceof Set ? savedIds : new Set(savedIds || []);
  const ordered = [...selectedIds];
  const added = ordered.filter((id) => !saved.has(id)).reverse();
  return [...added, ...ordered.filter((id) => saved.has(id))];
};

/** Ids between two positions of the visible list (shift-click range). */
export const rangeBetween = (orderedIds, fromId, toId) => {
  const from = orderedIds.indexOf(fromId);
  const to = orderedIds.indexOf(toId);
  if (from === -1 || to === -1) return [toId];
  const [start, end] = from < to ? [from, to] : [to, from];
  return orderedIds.slice(start, end + 1);
};

// ── Filters ──────────────────────────────────────────────────────────────────

export const EMPTY_FILTERS = Object.freeze({
  search: "",
  categoryId: "",
  brandId: "",
  family: "",
  stock: "",
  sale: "",
  addedWithinDays: "",
  meta: "",
});

export const DEFAULT_SORT = "newest";

/** Filters that live in the "More filters" panel (category/brand sit inline). */
export const PANEL_FILTER_KEYS = ["family", "stock", "sale", "addedWithinDays", "meta"];

export const countActiveFilters = (filters, keys = Object.keys(EMPTY_FILTERS)) =>
  keys.filter((key) => key !== "search" && String(filters?.[key] ?? "") !== "").length;

/** Query params for GET /marketing/meta/products, omitting empty values. */
export const buildPickerParams = (filters, sort, page, pageSize = PICKER_PAGE_SIZE) => {
  const params = { page, pageSize, sort: sort || DEFAULT_SORT };
  Object.entries(filters || {}).forEach(([key, value]) => {
    const trimmed = typeof value === "string" ? value.trim() : value;
    if (trimmed !== "" && trimmed !== null && trimmed !== undefined) params[key] = trimmed;
  });
  return params;
};

const normalizeText = (value) =>
  String(value || "")
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase();

/** Client-side match for the "Selected" view, mirroring the server search. */
export const matchesProductSearch = (product, search) => {
  const tokens = normalizeText(search).split(/\s+/).filter(Boolean);
  if (!tokens.length) return true;
  const haystack = normalizeText(
    [product.title, product.colorName, product.sku, product.brandName, product.categoryName].filter(Boolean).join(" ")
  );
  return tokens.every((token) => haystack.includes(token));
};

// ── Status presentation ──────────────────────────────────────────────────────

/**
 * Words for where a product set's changes are. We only know when Meta last
 * *downloaded* the catalog, never whether it processed it, so nothing here
 * ever claims "synced" or "sent".
 */
export const FEED_STATE_META = {
  empty: { tone: "neutral", labelKey: "stateEmpty", hintKey: "stateEmptyHint" },
  ready: { tone: "info", labelKey: "stateReady", hintKey: "stateReadyHint" },
  downloaded: { tone: "success", labelKey: "stateDownloaded", hintKey: "stateDownloadedHint" },
  unsaved: { tone: "warning", labelKey: "stateUnsaved", hintKey: "stateUnsavedHint" },
};

export const getFeedStateKey = (productSet, isDirty = false) => {
  if (isDirty) return "unsaved";
  return FEED_STATE_META[productSet?.feedState] ? productSet.feedState : "ready";
};

export const REACH_ISSUE_KEYS = {
  NOT_PUBLISHED: "issueNotPublished",
  NOT_IN_META_CATALOG: "issueNotInCatalog",
  NO_PRICE: "issueNoPrice",
  NO_IMAGE: "issueNoImage",
};

/**
 * Groups the saved products that will not appear in Meta by their *first*
 * (most fundamental) issue, so each product is counted once.
 */
export const summarizeReachIssues = (products = []) => {
  const groups = {};
  products.forEach((product) => {
    const issue = product?.metaReach?.issues?.[0];
    if (!issue) return;
    groups[issue] = (groups[issue] || 0) + 1;
  });
  // Mirrors the server: only published products are switched on in bulk. The
  // switch is per model, so colours of one model count once.
  const fixableInCatalog = new Set(
    products
      .filter((product) => !product.inMetaCatalog && product.status === "published")
      .map((product) => product.productId)
  ).size;
  return {
    total: Object.values(groups).reduce((sum, count) => sum + count, 0),
    groups: Object.keys(REACH_ISSUE_KEYS)
      .filter((issue) => groups[issue])
      .map((issue) => ({ issue, count: groups[issue] })),
    fixableInCatalog,
  };
};

export const STOCK_LEVEL_META = {
  OK: { tone: "neutral", labelKey: "stockIn" },
  LOW_STOCK: { tone: "warning", labelKey: "stockLow" },
  OUT_OF_STOCK: { tone: "danger", labelKey: "stockOut" },
};

// ── Errors ───────────────────────────────────────────────────────────────────

export const getApiErrorCode = (error) => String(error?.data?.error || "");
export const getApiStatus = (error) => Number(error?.status || error?.data?.code) || null;

/**
 * Plain-language message for a failed request; `fallbackKey` names what
 * failed ("Could not save the product selection.").
 */
export const humanizeMetaAdsError = (error, tt, fallbackKey) => {
  const code = getApiErrorCode(error);
  const status = getApiStatus(error);
  if (code === "PRODUCT_SET_NAME_TAKEN") return tt("errNameTaken");
  if (code === "PRODUCT_SET_NOT_FOUND" || status === 404) return tt("errNotFound");
  if (code === "PRODUCT_SET_CHANGED") return tt("errChanged");
  if (status === 403) return tt("errForbidden");
  if (status === 401) return tt("errSession");
  if (error?.status === "FETCH_ERROR") return `${tt(fallbackKey)} ${tt("errOffline")}`;
  return tt(fallbackKey);
};
